/**
 * Hardware-only configuration (`app.json`).
 *
 * Describes physical devices attached to the controller and how to reach
 * them: buses, meters, batteries. Resource definitions and market
 * participation live in `resource.json` (see `resourceConfigSchema.ts`)
 * and reference the hardware ids declared here.
 */
export interface AppConfig {
  /**
   * Protocol bus configurations. Omit if there are no buses configured.
   */
  protocolBuses?: ProtocolBusConfig[]
  /**
   * Power meter configurations. Omit if there are no power meters configured.
   *
   * Current limitation: only one Schneider Electric or Algodue Elettronica power meter can be added.
   */
  powerMeters?: PowerMeter[]
  /**
   * Battery configurations. Omit if there are no batteries configured.
   */
  batteries?: Battery[]
  /**
   * MQTT fallback, used only when `device-config.json` is missing on the
   * controller. Omit it otherwise.
   */
  mqtt?: MqttConfig
  /**
   * Plus/minus tolerance around the nominal 1 s steering-success sample
   * cadence, before a sample is excluded as clock drift or a jump.
   * A duration string, e.g. `"100 ms"`.
   *
   * Controller-wide rather than per-resource, because the causes (scheduler
   * stall, NTP correction, clock jump) are properties of how the controller
   * ticks rather than of any asset. Omit it to leave the controller on its
   * built-in default.
   */
  steeringTickJitterTolerance?: string
}

/**
 * MQTT connection settings. Only read when the controller has no
 * `device-config.json`; normally that file supplies these.
 */
export interface MqttConfig {
  /** Broker URL, e.g. `"ssl://fi-1.mqtt.staging.sympower.net:1884"`. */
  serverUrl?: string
  /** Numeric client id identifying this controller instance. */
  clientId?: string
  /** Broker authentication username. */
  username?: string
  /** Broker authentication password. */
  password?: string
  /** Equipment number for the SymMessage header. */
  equipmentNumber?: number
  /** Location code (e.g. `"SE1"`) for the SymMessage header. */
  location?: string
}

// ---------------------------------------------------------------------------
// Protocol bus configuration
// ---------------------------------------------------------------------------

export type ProtocolBusConfig = ModbusTcpBusConfig | ModbusRtuBusConfig

export enum ProtocolBusType {
  ModbusTcp = 'modbus-tcp',
  ModbusRtu = 'modbus-rtu',
}

export interface ProtocolBusConfigCommon {
  /** Stable handle referenced from other config (e.g. `busRef`). */
  id: string
  type: ProtocolBusType
}

export interface ModbusTcpBusConfig extends ProtocolBusConfigCommon {
  type: ProtocolBusType.ModbusTcp
  /** TCP host (IP address or hostname) */
  host: string
  /** TCP port (default: 502) */
  port?: number
}

export interface ModbusRtuBusConfig extends ProtocolBusConfigCommon {
  type: ProtocolBusType.ModbusRtu
  /** Logical serial port (default: "Port 1") */
  serialPort?: SerialPort
  /** Baud rate (default: 9600) */
  baudRate?: BaudRate
  /** Serial line mode (default: "8N1") */
  serialMode?: RtuSerialMode
}

export enum SerialPort {
  Port1 = 'Port 1',
  Port2 = 'Port 2',
}

export enum BaudRate {
  Baud9600 = 9600,
  Baud19200 = 19200,
  Baud38400 = 38400,
  Baud57600 = 57600,
  Baud115200 = 115200,
}

export enum RtuSerialMode {
  Mode_7N1 = '7N1',
  Mode_7E1 = '7E1',
  Mode_7O1 = '7O1',
  Mode_8N1 = '8N1',
  Mode_8N2 = '8N2',
  Mode_8E1 = '8E1',
  Mode_8O1 = '8O1',
}

// ---------------------------------------------------------------------------
// Battery
// ---------------------------------------------------------------------------

export type Battery = NwBattery | UbreAmptankBattery

export enum BatteryModel {
  NW = 'NW',
  UbreAmptank = 'UBRE_AMPTANK',
}

export interface BatteryCommon {
  /** Stable handle referenced from `resource.json` via `batteryRef`. */
  id: string
  model: BatteryModel
}

export interface NwBattery extends BatteryCommon {
  model: BatteryModel.NW
  /** Id of a `ProtocolBusConfig` declared in `protocolBuses` that the battery is reachable on. */
  busRef: string
  /**
   * Operating mode (default: "normal"). Today this is a startup-only knob —
   * there is no runtime command to change it, so deployments that need a
   * non-normal mode pin it here. In real deployments the value is always "normal".
   */
  mode?: NwBatteryMode
}

export enum NwBatteryMode {
  Normal = 'normal',
  ForceStandBy = 'force-stand-by',
  WatchdogOnly = 'watchdog-only',
  ReadOnly = 'read-only',
  ManualTest = 'manual-test',
}

/**
 * UBRE "Amptank" plant, reached through its Power Plant Controller.
 *
 * The model tag names the integration rather than the hardware: the register
 * map is a PLC programme written for this plant, not a Sungrow product
 * interface, so identical hardware elsewhere would need a different driver.
 *
 * Note there is no `mode` here — that is NW-only — and NW has no
 * `modbusUnitId`. The two are kept as separate members of the `Battery` union
 * so each keeps its own fields.
 */
export interface UbreAmptankBattery extends BatteryCommon {
  model: BatteryModel.UbreAmptank
  /** Id of a `ProtocolBusConfig` declared in `protocolBuses` that the plant is reachable on. */
  busRef: string
  /** Modbus unit id on the bus (default: 1). */
  modbusUnitId?: number
}

// ---------------------------------------------------------------------------
// Power meter
// ---------------------------------------------------------------------------

export enum PowerMeterModel {
  Generic = 'Generic',
  AccuvimII = 'Accuenergy Accuvim II',
  iEM3155 = 'Schneider Electric iEM3155',
  iEM3255 = 'Schneider Electric iEM3255',
  iEM3355 = 'Schneider Electric iEM3355',
  iEM3555 = 'Schneider Electric iEM3555',
  UPM209 = 'Algodue Elettronica UPM209',
  UPM307 = 'Algodue Elettronica UPM307',
}

export type PowerMeter =
  | GenericPowerMeter
  | AccuvimIIPowerMeter
  | IEM3155PowerMeter
  | IEM3255PowerMeter
  | IEM3355PowerMeter
  | IEM3555PowerMeter
  | UPM209PowerMeter
  | UPM307PowerMeter

export interface PowerMeterCommon {
  /** Stable handle referenced from `resource.json` via `meterRef`. */
  id: string
  /**
   * Model name from one of these supported ones:
   * - "Generic"
   * - "Accuenergy Accuvim II"
   * - "Schneider Electric iEM3155"
   * - "Schneider Electric iEM3255"
   * - "Schneider Electric iEM3355"
   * - "Schneider Electric iEM3555"
   * - "Algodue Elettronica UPM209"
   * - "Algodue Elettronica UPM307"
   *
   * You can use the "Generic" model when only configuring moving average or values interpretation.
   */
  model: PowerMeterModel
  /**
   * Optional moving average window configuration. Omit this if not used.
   */
  movingAverage?: MovingAverage
  /**
   * Optional power meter readings interpretation (multiply, adjust, absolute). Omit this if not needed.
   */
  values?: PowerValueInterpretation
}

export interface AlgodueOrSchneiderCommon extends PowerMeterCommon {
  /**
   * Optional power measurements reporting configuration for Schneider and Algodue power meters.
   *
   * If omitted, the default behaviour is to report once per minute and allow switching to once per second reporting.
   */
  reporting?: PowerMeasurementsReporting
}

/**
 * Allows configuring values and moving average without specifying the model
 */
export interface GenericPowerMeter extends PowerMeterCommon {
  model: PowerMeterModel.Generic
}

export interface AccuvimIIPowerMeter extends PowerMeterCommon {
  model: PowerMeterModel.AccuvimII
  /** Id of a `ProtocolBusConfig` declared in `protocolBuses` that this meter is reachable on. */
  busRef: string
  /** Modbus unit id on the bus (default: 1) */
  modbusUnitId?: number
  /** Optional power measurements reporting configuration. */
  reporting?: PowerMeasurementsReporting
  /** Metering configuration, specific to Accuvim II. */
  metering?: AccuvimIIMeteringConfig
}

export interface AccuvimIIMeteringConfig {
  /** Scaling factor applied to power meter readings (default: 1.0) */
  scalingFactor?: number
}

export interface UPM209PowerMeter extends AlgodueOrSchneiderCommon {
  model: PowerMeterModel.UPM209
  /** Metering configuration, specific to UPM 209 */
  metering?: UPM209MeteringConfig
}

export interface UPM307PowerMeter extends AlgodueOrSchneiderCommon {
  model: PowerMeterModel.UPM307
  /** Metering configuration, specific to UPM 307 */
  metering?: UPM307MeteringConfig
}

export interface IEM3155PowerMeter extends AlgodueOrSchneiderCommon {
  model: PowerMeterModel.iEM3155
  /** Metering configuration, specific to iEM3155 */
  metering?: IEM3155MeteringConfig
}

export interface IEM3255PowerMeter extends AlgodueOrSchneiderCommon {
  model: PowerMeterModel.iEM3255
  /** Metering configuration, specific to iEM3255 */
  metering?: IEM3255MeteringConfig
}

export interface IEM3355PowerMeter extends AlgodueOrSchneiderCommon {
  model: PowerMeterModel.iEM3355
  /** Metering configuration, specific to iEM3355 */
  metering?: IEM3355MeteringConfig
}

export interface IEM3555PowerMeter extends AlgodueOrSchneiderCommon {
  model: PowerMeterModel.iEM3555
  /** Metering configuration, specific to iEM3555 */
  metering?: IEM3555MeteringConfig
}

export enum UPM209Variant {
  DirectConnection = 'Direct connection',
  CTConnection = 'CT connection',
  RogowskiCoilsConnection = 'Rogowski coils connection',
}

export type UPM209MeteringConfig =
  | UPM209MeteringConfigForDirectConnection
  | UPM209MeteringConfigForCTConnection
  | UPM209MeteringConfigForRogowskiCoilsConnection

export interface UPM209MeteringConfigCommon {
  /**
   * Variant of UPM209 model, one of:
   * - 'direct connection'
   * - 'current transformer connection'
   * - 'Rogowski coils'
   */
  variant: UPM209Variant
  /**
   * Wiring mode, one of:
   * - '3 phases, 4 wires, 3 currents'
   * - '3 phases, 3 wires, 2 currents'
   * - '1 phase'
   */
  wiringMode: UPM209WiringMode
}

export enum UPM209WiringMode {
  PH3_W4_C3 = '3 phases, 4 wires, 3 currents',
  PH3_W3_C2 = '3 phases, 3 wires, 2 currents',
  PH1 = '1 phase',
}

export interface UPM209MeteringConfigForDirectConnection extends UPM209MeteringConfigCommon {
  variant: UPM209Variant.DirectConnection
}

export interface UPM209MeteringConfigForCTConnection extends UPM209MeteringConfigCommon {
  variant: UPM209Variant.CTConnection
  /**
   * CT primary current, same for all phases ("1 A" ... "50000 A")
   */
  ctPrimary: string
  /**
   * CT secondary current, same for all phases, one of:
   * - "1 A"
   * - "5 A"
   */
  ctSecondary: UPM209CTSecondary
  /** PT primary voltage ("1 V" .. "999999 V") */
  ptPrimary: string
  /** PT secondary voltage ("80 V" .. "150 V") */
  ptSecondary: string
}

export enum UPM209CTSecondary {
  CT_Secondary_1A = '1 A',
  CT_Secondary_5A = '5 A',
}

export interface UPM209MeteringConfigForRogowskiCoilsConnection extends UPM209MeteringConfigCommon {
  variant: UPM209Variant.RogowskiCoilsConnection
  /**
   * Rogowski full scale current (FSA), same for all phases, one of:
   * - "500 A"
   * - "4000 A"
   * - "20000 A"
   */
  currentFullScale: UPM209RogowskiCoilsFSA
  /** PT primary voltage ("1 V" ... "999999 V") */
  ptPrimary: string
  /** PT secondary voltage ("80 V" ... "150 V") */
  ptSecondary: string
}

export enum UPM209RogowskiCoilsFSA {
  FSA_500A = '500 A',
  FSA_4000A = '4000 A',
  FSA_20000A = '20000 A',
}

export interface UPM307MeteringConfig {
  /** CT ratio (1 - 9999) */
  ctRatio: number
  /**
   * FSA value, one of:
   * - "1 A"
   * - "5 A"
   */
  fsa: UPM307FSA
  /** PT ratio (1 - 9999) */
  ptRatio: number
  /**
   * Wiring mode, one of:
   * - "1 phase"
   * - "3 phases, 3 wires, 3 CTs"
   * - "3 phases, 4 wires, 3 CTs"
   * - "3 phases, 3 wires, 1 CT"
   * - "3 phases, 2 wires, 1 CT"
   */
  wiringMode: UPM307WiringMode
}

export enum UPM307FSA {
  FSA_1A = '1 A',
  FSA_5A = '5 A',
}

export enum UPM307WiringMode {
  PH1 = '1 phase',
  PH3_W3_CT3 = '3 phases, 3 wires, 3 CTs',
  PH3_W4_CT3 = '3 phases, 4 wires, 3 CTs',
  PH3_W3_CT1 = '3 phases, 3 wires, 1 CT',
  PH3_W2_CT1 = '3 phases, 2 wires, 1 CT',
}

export interface IEM3000MeteringConfigCommon {
  /** Nominal frequency (e.g. "50 Hz") */
  nominalFrequency: string
  /**
   * Wiring mode, one of:
   * - "1 phase, 2 wires, L-N"
   * - "1 phase, 2 wires, L-L"
   * - "1 phase, 3 wires, L-L with N"
   * - "3 phases, 3 wires"
   * - "3 phases, 4 wires"
   * - "1 phase, 4 wires, multi L with N"
   */
  wiringMode: IEM3000WiringMode
}

export interface IEM3000VTConnectionCommon {
  /**
   * Connection type, one of:
   * - "Direct Connect"
   * - "3 phases, 3 wires, 2 VTs"
   * - "3 phases, 4 wires, 3 VTs"
   */
  type: IEM3000VTConnectionType
}

export interface IEM3000VTDirectConnection extends IEM3000VTConnectionCommon {
  type: IEM3000VTConnectionType.DirectConnect
}

export interface IEM3000VTNonDirectConnection extends IEM3000VTConnectionCommon {
  type: IEM3000VTConnectionType.PH3_W3_2VTS | IEM3000VTConnectionType.PH3_W4_3VTS
  /**
   * VT primary voltage (from VT secondary voltage up to "1000000 V")
   */
  vtPrimary: string
  /**
   * VT secondary voltage, one of:
   * - "100 V"
   * - "110 V"
   * - "115 V"
   * - "120 V"
   */
  vtSecondary: IEM3000VTSecondary
}

export enum IEM3000VTSecondary {
  VT_Secondary_100V = '100 V',
  VT_Secondary_110V = '110 V',
  VT_Secondary_115V = '115 V',
  VT_Secondary_120V = '120 V',
}

export interface IEM3000MeteringConfigWithVTs extends IEM3000MeteringConfigCommon {
  vtConnection: IEM3000VTDirectConnection | IEM3000VTNonDirectConnection
}

export type IEM3155MeteringConfig = IEM3000MeteringConfigCommon

export interface IEM3255MeteringConfig extends IEM3000MeteringConfigWithVTs {
  /** Number of CTs */
  numberOfCTs: number
  /** CT primary current ("1 A" ... "32767 A") */
  ctPrimary: string
  /**
   * CT secondary current, one of:
   * - "1 A"
   * - "5 A"
   */
  ctSecondary: IEM3255CTSecondary
}

export enum IEM3255CTSecondary {
  CT_Secondary_1A = '1 A',
  CT_Secondary_5A = '5 A',
}

export type IEM3355MeteringConfig = IEM3000MeteringConfigCommon

export interface IEM3555MeteringConfig extends IEM3000MeteringConfigWithVTs {
  /** Number of CTs */
  numberOfCTs: number
}

export enum IEM3000WiringMode {
  PH1_W2_L_N = '1 phase, 2 wires, L-N',
  PH1_W2_L_L = '1 phase, 2 wires, L-L',
  PH1_W3_L_L_with_N = '1 phase, 3 wires, L-L with N',
  PH3_W3 = '3 phases, 3 wires',
  PH3_W4 = '3 phases, 4 wires',
  PH1_W4_multi_L_with_N = '1 phase, 4 wires, multi L with N',
}

export enum IEM3000VTConnectionType {
  DirectConnect = 'Direct Connect',
  PH3_W3_2VTS = '3 phases, 3 wires, 2 VTs',
  PH3_W4_3VTS = '3 phases, 4 wires, 3 VTs',
}

export interface PowerValueInterpretation {
  /** Whether to take the absolute value of the readings (default: false) */
  absolute?: boolean
  /** A number to add to the readings (default: 0.0) */
  adjustBy?: number
  /** A number to multiply the readings by (default: 1.0) */
  multiplyBy?: number
}

export interface MovingAverage {
  /** The moving average window period (e.g. "100 ms") */
  windowPeriod: string
}

export interface PowerMeasurementsReporting {
  /**
   * Whether to report power to the cloud on significant difference (default: false)
   *
   * More info: https://app.notion.com/p/sympower/Configuration-options-of-app-json-25e28c3d46fa80eca3c1de9552eb5b47#2b828c3d46fa805caa7aedd55c7b2d2c
   */
  basedOnSignificantDifference?: boolean
}
